import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  AlertOctagon,
  Paperclip,
  Send,
  ArrowRight,
  Image as ImageIcon,
  FileText,
  Mic,
  Video as VideoIcon,
  X,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Pencil,
  Save,
  XCircle,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  DragOverlay,
  pointerWithin,
} from "@dnd-kit/core";
import api from "../lib/api";
import { uploadUrl } from "../lib/uploads";
import Modal from "../components/Modal";
import DateTimePicker from "../components/DateTimePicker";
import BlurTextarea from "../components/BlurTextarea";
import { useAuth } from "../context/AuthContext";
import { usePersistedState } from "../lib/persisted";
import { AddToCalendarButton } from "../lib/calendarExport";
import VoiceQuickCreate from "../components/VoiceQuickCreate";
import UnseenDot from "../components/UnseenDot";
import useUnseen from "../lib/useUnseen";
import FromChatPill from "../components/chat/FromChatPill";
import useFocusOnMount from "../lib/useFocusOnMount";
import { toast } from "../components/ui/sonner";

const PRIORITIES = [
  { v: "urgent", label: "Urgent", color: "#F43F5E", icon: AlertOctagon },
  { v: "high", label: "High", color: "#F59E0B", icon: AlertOctagon },
  { v: "normal", label: "Normal", color: "#1FB6C1", icon: Circle },
  { v: "low", label: "Low", color: "#647087", icon: Circle },
];

const STATUSES = {
  todo: { label: "To do", color: "#647087" },
  in_progress: { label: "In progress", color: "#3B82F6" },
  completed: { label: "Completed", color: "#10B981" },
  rejected: { label: "Rejected", color: "#F43F5E" },
};

export default function Tasks() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const navigateToTask = (t) => {
    if (!t) return;
    navigate(`/tasks/${t.id}`);
  };
  const [search, setSearch] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const exitSelect = () => { setSelectMode(false); clearSelection(); };
  const bulkSetStatus = async (status) => {
    if (selectedIds.size === 0) return;
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map((id) => api.patch(`/tasks/${id}/status`, { status })));
      const label = status === "completed" ? "completed" : status === "todo" ? "marked to-do" : status;
      toast.success(`${ids.length} task${ids.length === 1 ? "" : "s"} ${label}.`);
    } catch (_e) {
      toast.error("Some tasks could not be updated.");
    } finally {
      exitSelect();
      load();
    }
  };

  const load = async () => {
    const [t, u] = await Promise.all([
      api.get("/tasks"),
      api.get("/users"),
    ]);
    setTasks(Array.isArray(t.data) ? t.data : []);
    setUsers(u.data);
  };

  useEffect(() => {
    load();
  }, []);

  // Awareness Contract — DESIGN_CONTRACT §7.
  const { isUnseen, touch, dismiss } = useUnseen("tasks", tasks);
  useEffect(() => { touch(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useFocusOnMount({ ready: tasks.length > 0, deps: [tasks.length] });

  // group tasks by assigned user (one section per user)
  const byUser = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      map[u.id] = { user: u, active: [], recently_completed: [], archive: [] };
    });
    const twoDaysAgo = Date.now() - 2 * 24 * 3600 * 1000;
    const q = search.trim().toLowerCase();
    const matches = (t) => {
      if (!q) return true;
      const hay = [t.title, t.description, t.priority, t.status, t.section, t.notes]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    };
    for (const t of tasks) {
      if (!matches(t)) continue;
      const ids = (t.assigned_to_ids || []).length ? t.assigned_to_ids : [t.created_by_id];
      for (const uid of ids) {
        if (!map[uid]) continue;
        if (t.status === "completed") {
          const ts = t.completed_at ? new Date(t.completed_at).getTime() : 0;
          if (ts >= twoDaysAgo) map[uid].recently_completed.push(t);
          else map[uid].archive.push(t);
        } else {
          map[uid].active.push(t);
        }
      }
    }
    // Sort within active: explicit sort_order first (drag-arranged), then
    // by priority + recency for items that haven't been hand-arranged.
    const prioRank = { urgent: 0, high: 1, normal: 2, low: 3 };
    Object.values(map).forEach((sec) => {
      sec.active.sort((a, b) => {
        const ao = a.sort_order;
        const bo = b.sort_order;
        if (ao != null && bo != null && ao !== bo) return ao - bo;
        if (ao != null && bo == null) return -1;
        if (ao == null && bo != null) return 1;
        return (
          (prioRank[a.priority] ?? 9) - (prioRank[b.priority] ?? 9) ||
          new Date(b.created_at) - new Date(a.created_at)
        );
      });
    });
    return map;
  }, [tasks, users, search]);

  // ---- Drag & drop reassignment ----
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor)
  );
  const [activeDragTask, setActiveDragTask] = useState(null);
  const [activeDragSection, setActiveDragSection] = useState(null);

  // Per-user section folding (persisted)
  const [foldedSections, setFoldedSections] = usePersistedState("tasks.foldedSections", {});
  const toggleFold = (uid) =>
    setFoldedSections((s) => ({ ...s, [uid]: !s[uid] }));

  // Per-user section ORDER (drag to sort) — persisted as array of userIds.
  // Stored locally for instant pickup AND mirrored to the user's profile
  // preferences so the order syncs across devices.
  const [sectionOrder, setSectionOrder] = usePersistedState("tasks.sectionOrder", []);
  // Hydrate from server preferences once on mount if local store is empty.
  useEffect(() => {
    if (sectionOrder.length > 0) return;
    const fromProfile = user?.preferences?.tasks_section_order;
    if (Array.isArray(fromProfile) && fromProfile.length > 0) {
      setSectionOrder(fromProfile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Compute the ordered user list:
  //   1. Logged-in user always at top by default (first time)
  //   2. Users explicitly placed via the persisted order
  //   3. Anyone else in original API order
  const orderedUsers = useMemo(() => {
    const byId = Object.fromEntries(users.map((u) => [u.id, u]));
    const seen = new Set();
    const out = [];
    // Honour persisted order first
    sectionOrder.forEach((uid) => {
      if (byId[uid] && !seen.has(uid)) {
        out.push(byId[uid]);
        seen.add(uid);
      }
    });
    // First-load default: put logged-in user first
    if (sectionOrder.length === 0 && user?.id && byId[user.id]) {
      out.push(byId[user.id]);
      seen.add(user.id);
    }
    // Append any remaining
    users.forEach((u) => {
      if (!seen.has(u.id)) out.push(u);
    });
    return out;
  }, [users, sectionOrder, user]);

  const handleDragStart = (event) => {
    const id = String(event.active.id);
    if (id.startsWith("section-drag-")) {
      const uid = id.slice("section-drag-".length);
      setActiveDragSection(users.find((u) => u.id === uid) || null);
      setActiveDragTask(null);
      return;
    }
    const t = tasks.find((x) => x.id === event.active.id);
    setActiveDragTask(t || null);
    setActiveDragSection(null);
  };

  const handleDragEnd = async (event) => {
    setActiveDragTask(null);
    setActiveDragSection(null);
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    // ---- Section reorder ----
    if (activeId.startsWith("section-drag-")) {
      const fromUid = activeId.slice("section-drag-".length);
      // Accept any of: section-drop-{uid}, section-drag-{uid}, or user-{uid}
      // — the latter happens because the section drop-zone shares its DOM
      // node with the task-reassignment drop-zone, and dnd-kit can pick
      // either one as the active over-target.
      let toUid = null;
      if (overId.startsWith("section-drop-")) toUid = overId.slice("section-drop-".length);
      else if (overId.startsWith("section-drag-")) toUid = overId.slice("section-drag-".length);
      else if (overId.startsWith("user-")) toUid = overId.slice("user-".length);
      if (!toUid || toUid === fromUid) return;

      const currentOrder = orderedUsers.map((u) => u.id);
      const fromIdx = currentOrder.indexOf(fromUid);
      const toIdx = currentOrder.indexOf(toUid);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = [...currentOrder];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      setSectionOrder(next);
      // Persist per-user via preferences so the order syncs across devices.
      api
        .patch("/users/me/preferences", { tasks_section_order: next })
        .catch(() => {});
      return;
    }

    // ---- Task reassignment (existing) ----
    const taskId = active.id;
    // Drop on another task → reorder within that user's section (if same user).
    if (typeof overId === "string" && !overId.startsWith("user-") && !overId.startsWith("section-")) {
      const draggedTask = tasks.find((t) => t.id === taskId);
      const targetTask = tasks.find((t) => t.id === overId);
      if (!draggedTask || !targetTask || draggedTask.id === targetTask.id) return;
      // Only allow reorder if they share at least one assignee — otherwise
      // treat it as a no-op (reassignment requires dropping on the section).
      const sharedUid = (draggedTask.assigned_to_ids || []).find((u) =>
        (targetTask.assigned_to_ids || []).includes(u),
      );
      if (!sharedUid) return;
      const section = byUser[sharedUid];
      if (!section) return;
      const ids = section.active.map((t) => t.id);
      const fromIdx = ids.indexOf(draggedTask.id);
      const toIdx = ids.indexOf(targetTask.id);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = [...ids];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      // Optimistic — patch sort_order locally so the UI snaps immediately.
      setTasks((prev) =>
        prev.map((t) => {
          const idx = next.indexOf(t.id);
          return idx >= 0 ? { ...t, sort_order: idx } : t;
        }),
      );
      try {
        await api.post("/tasks/reorder", { ordered_ids: next });
      } finally {
        load();
      }
      return;
    }
    const targetUserId = overId.startsWith("user-") ? overId.slice(5) : null;
    if (!targetUserId) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    if (task.assigned_to_ids?.length === 1 && task.assigned_to_ids[0] === targetUserId) return;
    const targetUser = users.find((u) => u.id === targetUserId);
    if (!targetUser) return;
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, assigned_to_ids: [targetUserId] } : t))
    );
    try {
      await api.post(`/tasks/${taskId}/transfer`, {
        to_user_id: targetUserId,
        note: `Reassigned to ${targetUser.name} (drag & drop)`,
      });
    } finally {
      load();
    }
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#647087]">Workboard</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <span className="w-2 h-2 rounded-full bg-[#A78BFA] animate-pulse" />
            Tasks
            <span className="text-sm text-[#A78BFA]/80 font-normal ml-1">{tasks.length}</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {!selectMode ? (
            <button
              type="button"
              onClick={() => setSelectMode(true)}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="tasks-select-mode-btn"
              title="Pick multiple tasks to mark complete"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Select
            </button>
          ) : (
            <button
              type="button"
              onClick={exitSelect}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="tasks-select-cancel"
            >
              <X className="w-3.5 h-3.5" /> Done
            </button>
          )}
          <VoiceQuickCreate
            kind="task"
            onCreate={async (fields) => {
              const { data } = await api.post("/tasks", {
                title: fields.title,
                description: fields.description,
                priority: fields.priority || "normal",
                status: "todo",
                assigned_to_ids: fields.assigned_to_ids || [],
                due_date: fields.due_date || null,
              });
              return data;
            }}
            onCreated={() => load()}
          />
          <Link to="/tasks/new" className="hv-btn hv-btn-primary" data-testid="task-new-btn">
            <Plus className="w-4 h-4" /> New task
          </Link>
        </div>
      </div>

      {/* Search bar — fuzzy filter across title/notes/priority/status */}
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search tasks…"
        className="hv-input max-w-md"
        data-testid="tasks-search"
      />

      {/* Bulk action toolbar (multi-select mode) */}
      {selectMode && (
        <div
          className="sticky top-2 z-30 flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-[#A78BFA]/40 bg-[#0e1015]/95 backdrop-blur"
          data-testid="tasks-bulk-toolbar"
        >
          <div className="text-xs text-[#a0aabf]">
            <span className="font-display text-base font-bold text-white tabular-nums">{selectedIds.size}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => bulkSetStatus("completed")}
              className="hv-btn hv-btn-primary text-xs disabled:opacity-40"
              data-testid="tasks-bulk-complete"
            >
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark complete
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={() => bulkSetStatus("todo")}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
              data-testid="tasks-bulk-todo"
            >
              <Circle className="w-3.5 h-3.5" /> Mark to-do
            </button>
            <button
              type="button"
              disabled={selectedIds.size === 0}
              onClick={clearSelection}
              className="hv-btn hv-btn-ghost text-xs disabled:opacity-40"
              data-testid="tasks-bulk-clear"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        </div>
      )}

      {/* Per-user sections */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => {
          setActiveDragTask(null);
          setActiveDragSection(null);
        }}
      >
        <div className="space-y-4">
          {orderedUsers.map((u) => {
            const sec = byUser[u.id];
            if (!sec) return null;
            if (sec.active.length === 0 && sec.recently_completed.length === 0) return null;
            return (
              <UserSection
                key={u.id}
                user={u}
                section={sec}
                onOpen={navigateToTask}
                reload={load}
                folded={!!foldedSections[u.id]}
                onToggleFold={() => toggleFold(u.id)}
                isMe={user?.id === u.id}
                isUnseen={isUnseen}
                dismissUnseen={dismiss}
                selectMode={selectMode}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
              />
            );
          })}
          {orderedUsers.every(
            (u) => byUser[u.id]?.active.length === 0 && byUser[u.id]?.recently_completed.length === 0,
          ) && (
            <div className="hv-card p-8 text-center text-[#647087]">
              No tasks yet — click <span className="text-[#A78BFA]">New task</span> to start.
            </div>
          )}
        </div>
        <DragOverlay dropAnimation={null}>
          {activeDragSection ? (
            <div className="hv-card px-3 py-2 shadow-2xl shadow-[#A78BFA]/20 border-[#A78BFA]/60 max-w-md cursor-grabbing">
              <div className="text-xs uppercase tracking-wider text-[#A78BFA] mb-0.5">
                Reordering section…
              </div>
              <div className="text-sm font-medium truncate">{activeDragSection.name}</div>
            </div>
          ) : activeDragTask ? (
            <div className="hv-card px-3 py-2 shadow-2xl shadow-[#A78BFA]/20 border-[#A78BFA]/60 max-w-md cursor-grabbing">
              <div className="text-xs uppercase tracking-wider text-[#A78BFA] mb-0.5">
                Reassigning…
              </div>
              <div className="text-sm font-medium truncate">{activeDragTask.title}</div>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Phase 7 Sub-iter 7C — TaskDetailModal extracted into a real
          page at /tasks/:id (see pages/team/TaskDetail.jsx).  Clicking
          a task row now navigates instead of opening a modal. */}
    </div>
  );
}

function UserSection({ user, section, onOpen, reload, folded, onToggleFold, isMe, isUnseen, dismissUnseen, selectMode, selectedIds, onToggleSelect }) {
  const [showArchive, setShowArchive] = useState(false);
  // Drop zone for task-reassignment (existing behavior)
  const { setNodeRef: setTaskDropRef, isOver: isTaskOver } = useDroppable({
    id: `user-${user.id}`,
  });
  // Drop zone for section-reorder (new)
  const { setNodeRef: setSectionDropRef, isOver: isSectionOver } = useDroppable({
    id: `section-drop-${user.id}`,
  });
  // Drag handle for section-reorder (new)
  const {
    attributes: dragAttrs,
    listeners: dragListeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({ id: `section-drag-${user.id}` });

  const initials = user.name
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  // Combine the section drop ref + task drop ref onto the same container.
  const setMergedRef = (node) => {
    setTaskDropRef(node);
    setSectionDropRef(node);
  };

  return (
    <div
      ref={setMergedRef}
      className={`hv-card p-4 transition ${
        isTaskOver ? "ring-2 ring-[#A78BFA] bg-[#A78BFA]/[0.04]" : ""
      } ${isSectionOver ? "ring-2 ring-[#1FB6C1] bg-[#1FB6C1]/[0.04]" : ""} ${
        isDragging ? "opacity-30" : ""
      } ${isMe ? "border-[#A78BFA]/30" : ""}`}
      data-testid={`task-section-${user.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Section drag handle (re-orders sections) */}
        <button
          ref={setDragRef}
          {...dragAttrs}
          {...dragListeners}
          className="text-[#647087] hover:text-[#1FB6C1] cursor-grab active:cursor-grabbing -ml-1 p-1"
          title="Drag to reorder section"
          data-testid={`section-drag-handle-${user.id}`}
          aria-label="Drag to reorder section"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        <button
          type="button"
          onClick={onToggleFold}
          className="text-[#647087] hover:text-white p-1"
          data-testid={`section-fold-${user.id}`}
          title={folded ? "Expand section" : "Collapse section"}
        >
          {folded ? <ChevronDown className="w-4 h-4 -rotate-90" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        <div className="w-9 h-9 rounded-full bg-[#1fb6c1]/15 border border-[#1fb6c1]/40 flex items-center justify-center text-xs font-bold text-[#1fb6c1]">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold flex items-center gap-2">
            {user.name}
            {isMe && (
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/10">
                You
              </span>
            )}
          </div>
          <div className="text-[10px] text-[#647087] uppercase tracking-wider">
            {user.role} · {section.active.length} active · {section.recently_completed.length} just done
          </div>
        </div>
        {isTaskOver && (
          <div className="text-[10px] text-[#A78BFA] font-bold uppercase tracking-wider animate-pulse">
            Drop to reassign
          </div>
        )}
        {isSectionOver && !isTaskOver && (
          <div className="text-[10px] text-[#1FB6C1] font-bold uppercase tracking-wider animate-pulse">
            Drop to reorder
          </div>
        )}
      </div>

      {!folded && (
        <div className="mt-3">
          {/* Active */}
          <div className="space-y-2 min-h-[40px]">
            {section.active.length === 0 && (
              <div className="text-sm text-[#647087]">All clear.</div>
            )}
            {section.active.map((t) => (
              <TaskRow key={t.id} task={t} onClick={() => { dismissUnseen?.(t.id); onOpen(t); }} reload={reload} unseen={isUnseen?.(t)} selectMode={selectMode} selected={selectedIds?.has(t.id)} onToggleSelect={onToggleSelect} />
            ))}
          </div>

          {/* Recently completed */}
          {section.recently_completed.length > 0 && (
            <div className="mt-4 pt-3 border-t border-emerald-500/20">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-2">
                Just completed (last 2 days)
              </div>
              <div className="space-y-2 opacity-90">
                {section.recently_completed.map((t) => (
                  <TaskRow key={t.id} task={t} onClick={() => { dismissUnseen?.(t.id); onOpen(t); }} reload={reload} unseen={isUnseen?.(t)} selectMode={selectMode} selected={selectedIds?.has(t.id)} onToggleSelect={onToggleSelect} />
                ))}
              </div>
            </div>
          )}

          {/* Archive */}
          {section.archive.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/5">
              <button
                onClick={() => setShowArchive(!showArchive)}
                className="text-xs text-[#647087] hover:text-white inline-flex items-center gap-1"
              >
                <ChevronDown
                  className={`w-3 h-3 transition ${showArchive ? "rotate-180" : ""}`}
                />
                Completed archive ({section.archive.length}) · auto-deleted after 30 days
              </button>
              {showArchive && (
                <div className="space-y-2 mt-2 opacity-60">
                  {section.archive.map((t) => (
                    <TaskRow key={t.id} task={t} onClick={() => { dismissUnseen?.(t.id); onOpen(t); }} reload={reload} compact unseen={isUnseen?.(t)} selectMode={selectMode} selected={selectedIds?.has(t.id)} onToggleSelect={onToggleSelect} />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TaskRow({ task, onClick, reload, compact, unseen, selectMode, selected, onToggleSelect }) {
  const prio = PRIORITIES.find((p) => p.v === task.priority) || PRIORITIES[2];
  const status = STATUSES[task.status] || STATUSES.todo;
  const { attributes, listeners, setNodeRef: setDragRef, isDragging } = useDraggable({
    id: task.id,
    disabled: task.status === "completed" || selectMode,
  });
  // Wave 7-fix R1 — make every row a drop-target so users can drag tasks
  // onto other tasks within the same section to reorder.
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: task.id });
  const setMergedRef = (node) => {
    setDragRef(node);
    setDropRef(node);
  };
  const toggle = async (e) => {
    e.stopPropagation();
    const next = task.status === "completed" ? "todo" : "completed";
    await api.patch(`/tasks/${task.id}/status`, { status: next });
    reload();
  };
  const setProgress = async (e) => {
    e.stopPropagation();
    await api.patch(`/tasks/${task.id}/status`, { status: "in_progress" });
    reload();
  };
  return (
    <div
      ref={setMergedRef}
      onClick={selectMode ? (e) => { e.stopPropagation(); onToggleSelect?.(task.id); } : onClick}
      data-testid={`task-row-${task.id}`}
      data-focus-id={task.id}
      className={`flex items-center gap-2 p-3 rounded-md border border-white/5 hover:border-[#A78BFA]/40 hover:bg-white/[0.02] transition cursor-pointer ${
        isDragging ? "opacity-30" : ""
      } ${isOver ? "ring-2 ring-[#A78BFA]/60" : ""} ${unseen ? "hv-unseen" : ""} ${selected ? "ring-2 ring-[#1FB6C1] bg-[#1FB6C1]/[0.05]" : ""}`}
      style={{
        borderLeft: `3px solid ${prio.color}`,
      }}
    >
      {task.status !== "completed" && !selectMode && (
        <button
          {...listeners}
          {...attributes}
          onClick={(e) => e.stopPropagation()}
          className="text-[#647087] hover:text-[#A78BFA] cursor-grab active:cursor-grabbing px-0.5"
          title="Drag to reassign"
          data-testid={`task-drag-${task.id}`}
          aria-label="Drag handle"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      {selectMode ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect?.(task.id); }}
          aria-label="Select task"
          data-testid={`task-select-${task.id}`}
          className={`w-6 h-6 rounded flex items-center justify-center transition ${
            selected
              ? "bg-[#1FB6C1] border border-[#1FB6C1] text-[#0a0b0e]"
              : "border border-white/30 text-transparent hover:border-[#1FB6C1]"
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
        </button>
      ) : (
        <button
          onClick={toggle}
          className={`w-6 h-6 rounded-full flex items-center justify-center transition ${
            task.status === "completed"
              ? "bg-emerald-500/20 border border-emerald-400 text-emerald-300"
              : "border border-white/30 text-[#647087] hover:border-[#A78BFA] hover:text-[#A78BFA]"
          }`}
        >
          {task.status === "completed" ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : task.status === "in_progress" ? (
            <Clock className="w-4 h-4" />
          ) : (
            <Circle className="w-4 h-4" />
          )}
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div className={`font-medium truncate flex items-center gap-2 ${task.status === "completed" ? "line-through text-[#647087]" : ""}`}>
          <UnseenDot unseen={unseen} testId={`task-unseen-${task.id}`} />
          {task.title}
        </div>
        {!compact && task.description && (
          <div
            className="text-xs text-[#a0aabf] mt-0.5 leading-snug whitespace-pre-wrap line-clamp-2"
            data-testid={`task-row-instruction-${task.id}`}
          >
            {task.description}
          </div>
        )}
        {!compact && (
          <div className="text-[10px] text-[#647087] mt-1 flex items-center gap-2 flex-wrap">
            <span style={{ color: prio.color }}>● {prio.label}</span>
            {task.due_date && <span>· due {new Date(task.due_date).toLocaleDateString('en-GB')}</span>}
            {(task.attachments || []).length > 0 && (
              <span className="inline-flex items-center gap-1">
                · <Paperclip className="w-3 h-3" /> {task.attachments.length}
              </span>
            )}
            {(task.notes || []).length > 0 && (
              <span>· {task.notes.length} notes</span>
            )}
          </div>
        )}
      </div>
      <span
        className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold"
        style={{ background: `${status.color}1a`, color: status.color, border: `1px solid ${status.color}55` }}
      >
        {status.label}
      </span>
      {task.status === "todo" && (
        <button
          onClick={setProgress}
          className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
          title="Mark in progress"
        >
          Start
        </button>
      )}
    </div>
  );
}

function AttachmentPicker({ items, onAdd, onRemove }) {
  const fileRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const [recording, setRecording] = useState(false);

  const startAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        const f = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await onAdd(f);
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
      };
      rec.start();
      audioRecorderRef.current = rec;
      setRecording(true);
    } catch (e) {
      alert("Microphone access denied or unsupported");
    }
  };
  const stopAudio = () => audioRecorderRef.current?.stop();

  return (
    <div className="space-y-2 mt-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="task-attach-file-btn"
        >
          <Paperclip className="w-3.5 h-3.5" /> File / image / video
        </button>
        {!recording ? (
          <button
            type="button"
            onClick={startAudio}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="task-record-audio-btn"
          >
            <Mic className="w-3.5 h-3.5" /> Record voice note
          </button>
        ) : (
          <button
            type="button"
            onClick={stopAudio}
            className="hv-btn text-xs bg-rose-500/15 border border-rose-500/40 text-rose-200"
          >
            <Mic className="w-3.5 h-3.5 animate-pulse" /> Stop recording
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,video/*,audio/*,application/pdf,.txt,.csv,.doc,.docx,.xls,.xlsx"
          multiple
          className="hidden"
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
              // eslint-disable-next-line no-await-in-loop
              await onAdd(f);
            }
            e.target.value = "";
          }}
        />
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {items.map((a) => (
            <AttachmentTile key={a.id} att={a} onRemove={() => onRemove(a.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentTile({ att, onRemove }) {
  const url = uploadUrl(att.id);
  const Icon =
    att.kind === "image" ? ImageIcon : att.kind === "audio" ? Mic : att.kind === "video" ? VideoIcon : FileText;
  return (
    <div className="border border-white/10 rounded-md p-2 bg-white/[0.02] flex items-center gap-2 group relative">
      {att.kind === "image" ? (
        <img src={url} alt="" className="w-12 h-12 object-cover rounded" />
      ) : att.kind === "video" ? (
        <video src={url} className="w-12 h-12 rounded bg-black" />
      ) : (
        <div className="w-12 h-12 rounded bg-white/5 border border-white/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#a0aabf]" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium truncate">{att.filename}</div>
        <div className="text-[10px] text-[#647087] uppercase">{att.kind}</div>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute -top-1.5 -right-1.5 bg-black border border-white/20 rounded-full p-0.5 text-rose-300 opacity-0 group-hover:opacity-100 transition"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

